/** Instance-owned state extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamState(runtime) {
  runtime.canvas = runtime.root.querySelector('#game');
  runtime.ctx =
    runtime.canvas.getContext('2d', { alpha: false, desynchronized: true }) ||
    runtime.canvas.getContext('2d');
  runtime.ui = {
    level: runtime.root.querySelector('#level'),
    balls: runtime.root.querySelector('#balls'),
    coins: runtime.root.querySelector('#coins'),
    score: runtime.root.querySelector('#score'),
    autoUpgrade: runtime.root.querySelector('#auto-upgrade'),
    launchState: runtime.root.querySelector('#launch-state'),
    angle: runtime.root.querySelector('#angle-label'),
    slider: runtime.root.querySelector('#angle-slider'),
    launch: runtime.root.querySelector('#launch'),
    pause: runtime.root.querySelector('#pause'),
    restart: runtime.root.querySelector('#restart'),
    highScoresOpen: runtime.root.querySelector('#high-scores-open'),
    rulesOpen: runtime.root.querySelector('#rules-open'),
    rulesScoresOpen: runtime.root.querySelector('#rules-scores-open'),
    rulesClose: runtime.root.querySelector('#rules-close'),
    rulesModal: runtime.root.querySelector('#rules-modal'),
    overlay: runtime.root.querySelector('#overlay'),
    overlayTitle: runtime.root.querySelector('#overlay-title'),
    overlayCopy: runtime.root.querySelector('#overlay-copy'),
    modeSelect: runtime.root.querySelector('#mode-select'),
    primary: runtime.root.querySelector('#primary-action'),
    scoreboard: runtime.root.querySelector('#scoreboard'),
    scoreboardEntry: runtime.root.querySelector('#scoreboard-entry'),
    scoreboardList: runtime.root.querySelector('#scoreboard-list'),
    scoreboardStatus: runtime.root.querySelector('#scoreboard-status'),
    playerName: runtime.root.querySelector('#player-name'),
    saveScore: runtime.root.querySelector('#save-score'),
    costs: {
      balls: runtime.root.querySelector('#cost-balls'),
      impact: runtime.root.querySelector('#cost-impact'),
      launchTime: runtime.root.querySelector('#cost-launch-time'),
      powerupChance: runtime.root.querySelector('#cost-powerup-chance'),
    },
    upgradeButtons: Array.from(runtime.root.querySelectorAll('.upgrade')),
    autoUpgradeLabel: runtime.root.querySelector('#auto-upgrade-label'),
    scoreboardTabs: Array.from(runtime.root.querySelectorAll('[data-board-range]')),
    modeButtons: Array.from(runtime.root.querySelectorAll('[data-game-mode]')),
    scoreboardModeTabs: Array.from(runtime.root.querySelectorAll('[data-board-mode]')),
  };
  runtime.W = 720;
  runtime.H = 1040;
  runtime.dangerY = runtime.H - 128;
  runtime.launcher = { x: runtime.W / 2, y: runtime.H - 52 };
  runtime.LAUNCHER_SPRITE_SIZE = 212;
  runtime.LAUNCHER_MUZZLE_DISTANCE = 94;
  runtime.GRID_COLS = 32;
  runtime.GRID_GAP = 3;
  runtime.GRID_MARGIN = 14;
  runtime.ROW_STEP = 23;
  runtime.BRICK_H = 18;
  runtime.SPATIAL_CELL = 42;
  runtime.mobileQualityQuery = window.matchMedia?.('(pointer: coarse), (max-width: 760px)');
  runtime.MOBILE_QUALITY = Boolean(runtime.mobileQualityQuery?.matches);
  runtime.EFFECT_QUALITY = runtime.MOBILE_QUALITY ? 0.58 : 1;
  runtime.MAX_PARTICLES = runtime.MOBILE_QUALITY ? 420 : 900;
  runtime.MAX_FLOATERS = runtime.MOBILE_QUALITY ? 45 : 70;
  runtime.MAX_BURSTS = runtime.MOBILE_QUALITY ? 46 : 80;
  runtime.CHAOS_EDGE_PARTICLES = runtime.MOBILE_QUALITY ? 36 : 72;
  runtime.GRAVITY_LUT_SIZE = 512;
  runtime.GRAVITY_REUSE_PROXIMITY = 0.72;
  runtime.LOW_POWER_BALL_THRESHOLD = 45;
  runtime.CHAOS_BALL_THRESHOLD = 80;
  runtime.PERF_DEBUG = new URLSearchParams(window.location.search).has('perf');
  runtime.DEFAULT_LAUNCH_WAIT_SECONDS = 20;
  runtime.MIN_LAUNCH_WAIT_SECONDS = 3;
  runtime.LAUNCH_WAIT_STEP_SECONDS = 0.5;
  runtime.LAUNCH_HUD_Y = runtime.H - 80;
  runtime.POWERUP_CHANCE_STEP = 0.01;
  runtime.POWERUP_CHANCE_MAX_BONUS = 0.2;
  runtime.SCOREBOARD_MAX_ENTRIES = 25;
  runtime.BOARD_CLEAR_CASH_PER_LEVEL = 100;
  runtime.BOARD_CLEAR_REFILL_ROWS = 10;
  runtime.BOARD_CLEAR_HIT_POWER_LEVEL_SHARE = 0.15;
  runtime.BALL_HIT_DAMAGE_CAP_FLOOR = 100;
  runtime.BALL_HIT_DAMAGE_CAP_BANDS = [
    { minLevel: 400, levelShare: 0.25, floor: 180 },
    { minLevel: 300, levelShare: 0.4, floor: 180 },
    { minLevel: 200, levelShare: 0.6, floor: 160 },
    { minLevel: 1, levelShare: 0.8, floor: runtime.BALL_HIT_DAMAGE_CAP_FLOOR },
  ];
  runtime.USER_LAUNCHED_BALL_CAP = 100;
  runtime.USER_LAUNCHED_BALL_GRACE_SECONDS = 0.75;
  runtime.DEFAULT_LAUNCH_QUEUE_INTERVAL_SECONDS = 0.075;
  runtime.RAPID_LAUNCH_QUEUE_INTERVAL_SECONDS = 0.1;
  runtime.NEUTRON_FUEL_INITIAL_COST = 5;
  runtime.NEUTRON_BOMB_DEPLOYMENT_SIZE = 20;
  runtime.NEUTRON_BOMB_POWER = 50;
  runtime.NEUTRON_CHAIN_BASE_MULTIPLIER = 50;
  runtime.NEUTRON_CHAIN_STEP_MULTIPLIER = 10;
  runtime.NEUTRON_COLLAPSE_BLOCK_SCORE_MULTIPLIER = 10;
  runtime.CHAOS_HIT_POWER_BASE = 2;
  runtime.CHAOS_HIT_POWER_LEVEL_DIVISOR = 10;
  runtime.SPEED_RUN_SECONDS = 120;
  runtime.SPEED_INITIAL_ADVANCE_BLOCKS = 32;
  runtime.SPEED_MAX_QUEUED_ROWS = 5;
  runtime.SPEED_QUEUED_ROW_INTERVAL_SECONDS = 1;
  runtime.SPEED_TIMED_PRESSURE_SECONDS = 5;
  runtime.SPEED_ENDGAME_WARNING_SECONDS = 15;
  runtime.TIMED_ROW_ADVANCE_SECONDS = 3;
  runtime.TIMED_ROW_ACCELERATION_SECONDS = 0.05;
  runtime.TIMED_ROW_ADVANCE_MIN_SECONDS = 1;
  runtime.TIMED_DANGER_WARNING_ROWS = 5;
  runtime.NEUTRON_SPEED_TIMER_BONUS_SECONDS = 5;
  runtime.NEUTRON_REWARD_POWERUPS = 3;
  runtime.SCOREBOARD_RANGES = {
    all: { label: 'All Time' },
    month: { label: 'This Month' },
    week: { label: 'This Week' },
    day: { label: 'Today' },
    hour: { label: 'This Hour' },
  };
  runtime.GAME_MODES = {
    classic: {
      label: 'Classic',
      overlayTitle: 'Pull, aim, release',
      overlayCopy:
        'Break numbered bricks before the maze reaches the danger rail. Collect glowing upgrades to flood the board with balls.',
      openingRows: 12,
      rapidLaunch: false,
      resetCombosOnLaunch: true,
      forceAutoUpgrade: false,
      endOnDangerRail: true,
    },
    speed: {
      label: 'Speed',
      overlayTitle: 'Two minute score rush',
      overlayCopy:
        'Auto upgrades are locked on. Tap to keep launching, keep combos alive, and race the 2 minute clock.',
      openingRows: 18,
      rapidLaunch: true,
      resetCombosOnLaunch: false,
      forceAutoUpgrade: true,
      durationSeconds: runtime.SPEED_RUN_SECONDS,
      bottomPenalty: true,
      endOnDangerRail: false,
    },
    timed: {
      label: 'Timed',
      overlayTitle: 'Outrun the pressure',
      overlayCopy:
        'Auto upgrades are locked on. Tap to keep launching while each pressure row arrives faster than the last.',
      openingRows: 12,
      rapidLaunch: true,
      resetCombosOnLaunch: false,
      forceAutoUpgrade: true,
      timedRowAdvanceSeconds: runtime.TIMED_ROW_ADVANCE_SECONDS,
      endOnDangerRail: true,
    },
  };
  runtime.PLAYER_BALL_BASE_DAMAGE_BY_MODE = {
    classic: 5,
    speed: 1,
    timed: 1,
  };
  runtime.NEUTRON_PLATITUDES = [
    'Helena Style',
    "You're Awesome",
    'Amazing Skills',
    'Legendary Aim',
    'Brilliant Shot',
    'Pure Genius',
    'Unstoppable',
    'Star Power',
    'Masterful',
    'Incredible Run',
  ];
  runtime.TRIG_SCALE = 2;
  runtime.TRIG_STEPS = 360 * runtime.TRIG_SCALE;
  runtime.FRAME_TARGETS = {
    active: 1000 / 60,
    mobilePressure: 1000 / 30,
    pressureGuard: 1000 / 24,
    ready: 1000 / 30,
    idle: 1000 / 15,
    paused: 1000 / 10,
  };
  runtime.ENERGY_MODES = {
    normal: { label: 'normal', effectScale: 1, maxParticles: runtime.MAX_PARTICLES },
    balanced: {
      label: 'balanced',
      effectScale: 0.72,
      maxParticles: Math.floor(runtime.MAX_PARTICLES * 0.72),
    },
    pressureGuard: {
      label: 'pressureGuard',
      effectScale: 0.46,
      maxParticles: Math.floor(runtime.MAX_PARTICLES * 0.46),
    },
    idle: {
      label: 'idle',
      effectScale: 0.28,
      maxParticles: Math.floor(runtime.MAX_PARTICLES * 0.28),
    },
  };
  runtime.ENERGY_FRAME_PRESSURE_MS = runtime.MOBILE_QUALITY ? 24 : 30;
  runtime.ENERGY_THERMAL_FRAME_MS = runtime.MOBILE_QUALITY ? 31 : 38;
  runtime.ENERGY_RECOVERY_FRAME_MS = runtime.MOBILE_QUALITY ? 17 : 20;
  runtime.ENERGY_PRESSURE_ENTITY_LOAD = runtime.MOBILE_QUALITY ? 92 : 150;
  runtime.ENERGY_THERMAL_ENTITY_LOAD = runtime.MOBILE_QUALITY ? 150 : 240;
  runtime.ENERGY_PRESSURE_SECONDS = 1.2;
  runtime.ENERGY_RECOVERY_SECONDS = 3.6;
  runtime.ENERGY_INTERACTION_BOOST_MS = 700;
  runtime.HUD_FAST_SYNC_MS = 110;
  runtime.HUD_SLOW_SYNC_MS = 260;
  runtime.rand = (min, max) => min + runtime.random() * (max - min);
  runtime.clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  runtime.trigLookup = Array.from({ length: runtime.TRIG_STEPS }, (_, index) => {
    const radians = (index / runtime.TRIG_SCALE) * (Math.PI / 180);
    return { cos: Math.cos(radians), sin: Math.sin(radians), radians };
  });
  runtime.trigDeg = (angle) =>
    runtime.trigLookup[
      ((Math.round(angle * runtime.TRIG_SCALE) % runtime.TRIG_STEPS) + runtime.TRIG_STEPS) %
        runtime.TRIG_STEPS
    ];
  runtime.scratchBallBuckets = new Map();
  runtime.scratchCheckedPairs = new Set();
  runtime.scratchNearby = [];
  runtime.neutronFuelGaugeSpriteCache = new Map();
  runtime.nebulaImage = new Image();
  runtime.turretImage = new Image();
  runtime.turretImage.src = '/assets/steam-punk/steampunk-turret-source.png';
  runtime.turretSprite = document.createElement('canvas');
  runtime.turretSprite.width = 256;
  runtime.turretSprite.height = 256;
  runtime.turretSpriteCtx = runtime.turretSprite.getContext('2d', { willReadFrequently: true });
  runtime.turretReady = false;
  runtime.ballSpriteCache = new Map();
  runtime.brickSpriteCache = new Map();
  runtime.planetSpriteCache = new Map();
  runtime.backdropCanvas = document.createElement('canvas');
  runtime.backdropCanvas.width = runtime.W;
  runtime.backdropCanvas.height = runtime.H;
  runtime.backdropCtx = runtime.backdropCanvas.getContext('2d');
  runtime.backdropReady = false;
  runtime.boardSceneCanvas = document.createElement('canvas');
  runtime.boardSceneCanvas.width = runtime.W;
  runtime.boardSceneCanvas.height = runtime.H;
  runtime.boardSceneCtx = runtime.boardSceneCanvas.getContext('2d');
  runtime.boardSceneReady = false;
  runtime.boardSceneDirty = true;
  runtime.nebulaImage.onload = () => {
    runtime.renderBackdropCache();
    runtime.markBoardSceneDirty();
    runtime.requestDraw();
  };
  runtime.turretImage.onload = () => {
    runtime.prepareTurretSprite();
    runtime.requestDraw();
  };
  runtime.game = {
    level: 1,
    score: 0,
    coins: 0,
    ballCount: 1,
    crit: 0,
    launchWaitSeconds: runtime.DEFAULT_LAUNCH_WAIT_SECONDS,
    powerupChanceBonus: 0,
    autoUpgrade: false,
    angle: 90,
    status: 'ready',
    balls: [],
    bricks: [],
    orbs: [],
    walls: [],
    planets: [],
    particles: [],
    bursts: [],
    floaters: [],
    screenFlash: 0,
    shake: 0,
    boardShiftCue: 0,
    boardShiftCueMax: 0,
    chaosTurns: 0,
    chaosStacks: 0,
    brickBuckets: new Map(),
    queryMark: 1,
    drawQuality: 'high',
    spatialDirty: true,
    launchQueue: 0,
    launchDelay: 0,
    launchSource: 'user',
    volleyStartedAt: 0,
    roundAdvancedForVolley: false,
    nextBallId: 1,
    nextPlanetId: 1,
    volleyActive: false,
    aiming: false,
    last: runtime.now(),
    lastTick: runtime.now(),
    lastError: '',
    lastMods: '',
    launchFlash: 0,
    lastLaunchIntentAt: 0,
    fps: 0,
    frameMs: 0,
    updateMs: 0,
    drawMs: 0,
    gravityCalls: 0,
    gravityTrapCalls: 0,
    lastFrameTime: 0,
    frameBudgetMs: runtime.FRAME_TARGETS.active,
    perfDebug: runtime.PERF_DEBUG,
    frameSample: { count: 0, elapsed: 0, draw: 0 },
    energyMode: runtime.MOBILE_QUALITY ? 'balanced' : 'normal',
    energyReason: runtime.MOBILE_QUALITY ? 'mobile pressure default' : 'normal',
    energyPressureSeconds: 0,
    energyRecoverySeconds: 0,
    energyLoad: 0,
    observedFps: 0,
    interactionBoostUntil: 0,
    activeUserBalls: 0,
    activeNeutronBombs: 0,
    hudLastSyncAt: 0,
    hudLastFastSyncAt: 0,
    hudSnapshot: {},
    effectQueue: [],
    autoUpgradeRunning: false,
    scoreboardRange: 'all',
    scoreboardMode: 'gameover',
    scoreboardRequestId: 0,
    scoreboardRows: [],
    scoreboardSource: 'web',
    currentRunId: '',
    scoreSavedForRun: false,
    boardClearRewarded: false,
    boardClears: 0,
    boardClearHitPower: 0,
    boardClearRefillRows: 0,
    mode: 'classic',
    scoreboardBoardMode: 'classic',
    modeRunStarted: false,
    modeTimerRemaining: 0,
    timedRowAdvanceElapsed: 0,
    timedCurrentRowAdvanceSeconds: runtime.TIMED_ROW_ADVANCE_SECONDS,
    destroyedBlocksSinceAdvance: 0,
    speedNextAdvanceBlocks: runtime.SPEED_INITIAL_ADVANCE_BLOCKS,
    speedQueuedRows: 0,
    speedQueuedRowTimer: 0,
    speedTimedPressureElapsed: 0,
    neutronFuel: 0,
    neutronFuelCost: runtime.NEUTRON_FUEL_INITIAL_COST,
    neutronDeployments: 0,
    neutronBombsInDeployment: 0,
    neutronStarChain: 0,
    neutronDeploymentId: 0,
    neutronPointerArmed: false,
    costs: { balls: 40, impact: 58, launchTime: 70, powerupChance: 92 },
    mods: ['Durability climbs every wave', 'Maze rails redirect shots', 'Powerups split volleys'],
  };
}
